import React, { memo, useState } from 'react';
import { Flex, Carousel } from 'antd-mobile';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { ImgWrap } from 'pubBiz';

const defaultProps = {
  data: {
    itemList: []
  },
  config: {},
  vertical: false,
  verticalConfig: {},
  horizontal: false,
  horizontalConfig: {
    type: 'swiper',
    showBullets: true,
    bulletsConfig: {
      marginTop: '0.04rem'
    }
  },
  onPress: null
};

// 推荐位列表项数据结构
const itemListPropTypes = PropTypes.arrayOf(
  PropTypes.shape({
    id: PropTypes.string, // 推荐位ID
    type: PropTypes.number, // 推荐位类型
    content: PropTypes.string, // 推荐位内容
    extend: PropTypes.object, // 扩展字段
    imgUrlApp: PropTypes.string // 推荐位图片
  })
);

// size成员结构
const sizePropTypes = PropTypes.shape({
  width: PropTypes.number.isRequired, // 推荐位宽度
  height: PropTypes.number.isRequired // 推荐位高度
});

// 横向布局配置
const horizontalConfigPropTypes = PropTypes.shape({
  size: sizePropTypes.isRequired, // 单个推荐位尺寸
  count: PropTypes.number.isRequired, // 每行显示个数
  showBullets: PropTypes.bool, // 是否显示轮播指示器
  type: PropTypes.string, // 排布类型 swiper: 轮播  scroll: 滑动
  bulletsConfig: PropTypes.shape({
    marginTop: PropTypes.number // 自定义指示器顶部间距
  })
});

const verticalConfigPropTypes = PropTypes.shape({
  size: sizePropTypes.isRequired, // 单个推荐位尺寸
  count: PropTypes.number.isRequired // 每行显示个数
});

const propTypes = {
  width: PropTypes.number.isRequired, // 容器宽度
  containerStyle: PropTypes.object, // 容器样式
  data: PropTypes.shape({
    itemList: itemListPropTypes.isRequired // 商品列表
  }),
  config: PropTypes.shape({
    radius: PropTypes.string // 圆角值
  }),
  horizontal: PropTypes.bool, // 是否横向布局
  horizontalConfig: horizontalConfigPropTypes, // 横向布局配置
  vertical: PropTypes.bool, // 是否纵向布局
  verticalConfig: verticalConfigPropTypes, // 纵向布局配置
  onPress: PropTypes.func // 点击事件回调
};

const ClassifyRecommandCell = memo((props) => {
  const {
    containerStyle, // Object 容器样式
    width: cellWidth, // Number 宽度
    data, // Object 数据源
    config, // Object 配置
    vertical, // Boolen false 纵向展示 true 横向展示
    horizontal, // Boolen false 纵向展示 true 横向展示
    horizontalConfig, // Object 横向配置
    verticalConfig, // Object 纵向配置
    onPress, // Function 点击事件
    autoplay: autoplayProp, // Boolean 自动轮播
    infinite: infiniteProp, // Boolean 无限轮播
    autoplayInterval: autoplayIntervalProp // Number 自动轮播间隔
  } = props;
  const [nowPage, setNowPage] = useState(1); // 当前页数

  const {
    itemList // Array 数据列表
  } = data;

  const {
    radius = 0 // String 圆角值
  } = config || {};

  // 点击事件
  const handlePress = (val) => {
    onPress && onPress(val);
  };

  const renderBullets = () => {
    const {
      count, // Number 每行内容数量
      showBullets = true, // Boolean 是否显示指示器
      bulletsConfig = {
        marginTop: '0.04rem'
      }
    } = horizontalConfig;
    if (!showBullets) return null;
    const list = _.chunk(itemList, count);

    // Carousel的页数
    const length = list.length || 1;

    // 指示器总宽度
    const viewW = '0.56rem';
    const nowW = `calc(${viewW} * (${nowPage / length}))`;

    return (
      <Flex
        direction="row"
        align="center"
        style={{
          alignSelf: 'center',
          width: viewW,
          height: '0.04rem',
          backgroundColor: '#F5F7FAaa',
          marginTop: bulletsConfig.marginTop
        }}>
        <div
          style={{
            width: nowW,
            height: '0.04rem',
            backgroundColor: '#fff'
          }}
        />
      </Flex>
    );
  };

  // 推荐位子项
  const renderRecommandsItem = (item, index) => {
    let config = horizontalConfig.size;
    if (horizontal) {
      config = horizontalConfig.size;
    } else if (vertical) {
      config = verticalConfig.size;
    }
    const { width, height } = config;

    return (
      <Flex
        key={`recommand_item_${index}`}
        style={{
          borderRadius: radius
        }}
        onClick={() => handlePress(item)}>
        <ImgWrap w={width} h={height} src={item.imgUrlApp || ''} mode="cover" />
      </Flex>
    );
  };

  // 纵向布局
  const renderVertical = () => {
    if (horizontal) return null;

    const {
      size, // Number 每行内容尺寸
      count // Number 每行内容数量
    } = verticalConfig;
    const { width } = size;
    let marginL = `calc((${cellWidth} - ${count} * ${width}) / ${count - 1})`;
    if (marginL < 0) {
      marginL = 0;
    }

    return (
      <Flex direction="row" justify="center" wrap="wrap">
        {itemList.map((item, index) => {
          return (
            <Flex
              key={index}
              style={{
                width,
                marginLeft: index % count != 0 ? marginL : 0,
                marginTop: index < count ? 0 : marginL
              }}>
              {renderRecommandsItem(item, index)}
            </Flex>
          );
        })}
      </Flex>
    );
  };

  // 横向布局
  const renderHorizontal = () => {
    if (vertical) return null;
    const {
      type = 'swiper' // String 排布类型
    } = horizontalConfig;

    return (
      <Flex>
        {/* 横向布局 */}
        {type == 'swiper' && renderSwiper()}
        {type == 'scroll' && renderScroll()}
      </Flex>
    );
  };

  // 横向布局-Swiper滚动
  const renderSwiper = () => {
    const {
      size, // Object 单个推荐位尺寸
      count // Number 每行内容数量
    } = horizontalConfig;
    const { width, height } = size;
    const list = _.chunk(itemList, count);
    let marginL = `calc((${cellWidth} - ${count} * ${width}) / ${count - 1})`;
    if (marginL < 0) {
      marginL = 0;
    }

    return (
      <Flex direction="column">
        <Carousel
          style={{ width: cellWidth, height }}
          autoplay={autoplayProp || false}
          infinite={infiniteProp || false}
          autoplayInterval={autoplayIntervalProp || 3000}
          dots={false}
          selectedIndex={nowPage - 1}
          afterChange={(index) => {
            setNowPage(index + 1);
          }}>
          {list.map((item, index) => {
            return (
              <Flex
                direction="row"
                key={index}
                style={{
                  width: cellWidth,
                  overflow: 'hidden'
                }}>
                {item.map((_item, _index) => {
                  return (
                    <Flex
                      key={_index}
                      style={{
                        marginLeft: _index != 0 ? marginL : 0
                      }}>
                      {renderRecommandsItem(_item, _index)}
                    </Flex>
                  );
                })}
              </Flex>
            );
          })}
        </Carousel>
        {renderBullets()}
      </Flex>
    );
  };

  // 横向布局-Scroll滚动
  const renderScroll = () => {
    let marginL = '0.12rem';
    const {
      size // Object 单个推荐位尺寸
    } = horizontalConfig;
    const { height } = size;
    return (
      <Flex style={{ width: cellWidth, height }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            overflowX: 'scroll',
            overflowY: 'hidden'
          }}>
          {itemList.map((item, index) => {
            return (
              <Flex
                key={index}
                style={{
                  flexShrink: 0,
                  marginLeft: index != 0 ? marginL : 0
                }}>
                {renderRecommandsItem(item, index)}
              </Flex>
            );
          })}
        </div>
      </Flex>
    );
  };

  return (
    <Flex style={containerStyle}>
      {/* 横向布局 */}
      {renderHorizontal()}
      {/* 纵向布局 */}
      {renderVertical()}
    </Flex>
  );
});

ClassifyRecommandCell.defaultProps = defaultProps;
ClassifyRecommandCell.propTypes = propTypes;

export default ClassifyRecommandCell;



// WEBPACK FOOTER //
// ./src/pubBiz/CommonCell/ClassifyRecommandCell/index.js